/**
 * Categories — Hub.
 *
 * Two choices: Today's Board (daily-seeded, same for everyone in the
 * community) and Practice (a fresh random board, no streak, no pressure).
 *
 * Aesthetic: blue board, brass trim, gold value tiles, and a compact
 * broadcast-style mode picker that matches the playable surface.
 */
import { motion } from 'motion/react';
import { useEffect } from 'react';
import { useNavigate } from 'react-router';

import { usePreferences } from '@/preferences';

import { cardSpring, pageSpring } from './motion';
import { sound } from './sound';

import './Hub.css';

interface ModeCard {
  slug: 'daily' | 'practice';
  title: string;
  eyebrow: string;
  blurb: string;
  cta: string;
  /** Sample dollar values to render as a teaser tile column. */
  values: ReadonlyArray<number>;
}

const MODES: ReadonlyArray<ModeCard> = [
  {
    slug: 'daily',
    title: "Today's Board",
    eyebrow: 'Together',
    blurb: 'A new board every day. Everyone in your community plays the same six categories.',
    cta: 'Play daily',
    values: [200, 400, 600, 800, 1000],
  },
  {
    slug: 'practice',
    title: 'Practice',
    eyebrow: 'Any time',
    blurb: 'A fresh board, as many as you like. No clock, no streak. Just answer in your own time.',
    cta: 'Start practice',
    values: [200, 400, 600, 800, 1000],
  },
];

export function CategoriesHub(): React.JSX.Element {
  const navigate = useNavigate();
  const { theme } = usePreferences();

  useEffect(() => {
    sound.unlock();
  }, []);

  const pick = (slug: 'daily' | 'practice'): void => {
    sound.unlock();
    sound.play('nav');
    navigate(`/play/categories?mode=${slug}`);
  };

  return (
    <div className="tb-hub" data-theme={theme}>
      <div className="tb-hub__page">
        <PageBackdrop />

        <motion.header
          className="tb-hub__head"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ ...pageSpring, delay: 0.05 }}
        >
          <p className="tb-hub__eyebrow">Game-show trivia</p>
          <h1 className="tb-hub__title">Categories</h1>
          <p className="tb-hub__lede">
            Pick a board, choose a dollar value, and solve each clue in question form.
          </p>
        </motion.header>

        <section className="tb-hub__choices" aria-label="Choose how to play">
          <ul className="tb-hub__cards" role="list">
            {MODES.map((m, i) => (
              <motion.li
                key={m.slug}
                className="tb-hub__card-item"
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ ...cardSpring, delay: 0.18 + i * 0.08 }}
              >
                <button
                  type="button"
                  className="tb-hub__card"
                  data-mode={m.slug}
                  onClick={() => pick(m.slug)}
                >
                  <div className="tb-hub__card-board" aria-hidden="true">
                    {m.values.map((v) => (
                      <span key={v} className="tb-hub__card-tile">
                        ${v}
                      </span>
                    ))}
                  </div>
                  <div className="tb-hub__card-meta">
                    <span className="tb-hub__card-eyebrow">{m.eyebrow}</span>
                    <span className="tb-hub__card-title">{m.title}</span>
                    <span className="tb-hub__card-blurb">{m.blurb}</span>
                    <span className="tb-hub__card-cta" aria-hidden="true">
                      {m.cta}
                    </span>
                  </div>
                </button>
              </motion.li>
            ))}
          </ul>
        </section>

        <footer className="tb-hub__footer">
          <span className="tb-hub__footer-rule" aria-hidden="true" />
          <p className="tb-hub__footer-text">
            Daily boards rotate for everyone. Practice boards are always fresh.
          </p>
        </footer>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Backdrop — atmospheric texture
// ---------------------------------------------------------------------------

function PageBackdrop(): React.JSX.Element {
  return (
    <div className="tb-hub__backdrop" aria-hidden="true">
      <svg width="100%" height="100%" aria-hidden="true" className="tb-hub__grain">
        <defs>
          <filter id="tb-hub-grain" x="0" y="0" width="100%" height="100%">
            <feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="2" seed="3" />
            <feColorMatrix values="0 0 0 0 0.16  0 0 0 0 0.12  0 0 0 0 0.08  0 0 0 0.06 0" />
          </filter>
        </defs>
        <rect width="100%" height="100%" filter="url(#tb-hub-grain)" />
      </svg>
    </div>
  );
}
